import { monthName, toDate, weekdayName, type ISODate } from '@app/shared'

/**
 * The same calendar day one year earlier — what the "On this day" card reads.
 *
 * Deliberately NOT `addDays(date, -365)`: across a leap year that lands on the day before, and an
 * "on this day" card showing the wrong day is a quiet lie that nobody would ever catch. Feb 29 has
 * no counterpart in a common year, so it answers Feb 28 rather than rolling into March.
 */
export function oneYearBefore(date: ISODate): ISODate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const year = y - 1
  const lastOfMonth = new Date(Date.UTC(year, m, 0, 12)).getUTCDate()
  const day = Math.min(d, lastOfMonth)
  return `${year}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** 'Tuesday, September 16, 2025' — the long date the "On this day" card carries, with its year. */
export function formatLongDateWithYear(date: ISODate): string {
  return `${weekdayName(date)}, ${monthName(date)} ${toDate(date).getUTCDate()}, ${date.slice(0, 4)}`
}

/** 'Sep 16, 2026' — the breadcrumb on the day page. */
export function formatShortDateWithYear(date: ISODate): string {
  return `${monthName(date, true)} ${toDate(date).getUTCDate()}, ${date.slice(0, 4)}`
}

/** The day-of-month number, as the day cards and the heatmap read it off the string. */
export const dayOfMonth = (date: ISODate): number => Number(date.slice(8, 10))

/**
 * When an entry was written, in the three words the day page uses. Read off the entry's own
 * timestamp — never guessed from the date, which would make every day "evening".
 */
export function partOfDay(timestamp: string): string {
  const hour = Number(timestamp.slice(11, 13))
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

/** Entry text as paragraphs. Blank lines separate them; a single blank line is enough. */
export const paragraphsOf = (text: string): string[] =>
  text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)

/**
 * THE HEATMAP'S FIVE STEPS. A day with no entry is level 0 and is a real cell, not a hole — the
 * archive's whole argument is that gaps are visible (README §2, "Gaps are honest").
 *
 * The thresholds are word counts, not percentiles: a percentile scale re-colours the entire year
 * every time one long day is written, which makes the picture unreadable across visits.
 */
export function heatLevel(words: number): 0 | 1 | 2 | 3 | 4 {
  if (words === 0) return 0
  if (words < 150) return 1
  if (words < 300) return 2
  if (words < 450) return 3
  return 4
}

export const HEAT_FILL = [
  'bg-heat-0',
  'bg-heat-1',
  'bg-heat-2',
  'bg-heat-3',
  'bg-heat-4',
] as const
